import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  betaUpdateGmailVacationmessage,
} from '../../../../../api/users-api';
import { handleErrors } from '../../../../../utils/errors';
import { VacationModel } from '../../../../../../types';
import { Controller, useForm } from 'react-hook-form';

import dayjs from 'dayjs';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import { useStore } from '../../../../state/zustand-store';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';
import TextEditor from '../../../../../shared/editor/text-editor';
import { Editor } from 'tinymce';
import { Messages } from '../../../../../utils/message-constants';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
interface VacationResponderFormProps {
  userEmail: string;
  userRoles: string[];
  vacationMessage: VacationModel;
  changeLogRoles: string[];
}

interface FormInput {
  enableAutoReply: boolean;
  restrictToContacts: boolean;
  restrictToDomain: boolean;
  dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null];
  responseSubject: string;
  responseBodyHtml: string;
}

export default function VacationResponderForm({
  userEmail,
  userRoles,
  vacationMessage,
  changeLogRoles,
}: VacationResponderFormProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const nextDay = useRef(dayjs().add(1, 'day'));
  const dayAfter = useRef(dayjs().add(2, 'day'));

  const userCanSetVacationMessage = isAbleToSeeUIElement(
    userRoles,
    UserRoles.SetGmailVacationMessage.value
  );

  const userCanToggleAutoReply = isAbleToSeeUIElement(
    userRoles,
    UserRoles.DisableGmailVacationMessage.value
  );

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isDirty, dirtyFields },
    watch,
    reset,
    getValues,
  } = useForm<FormInput>({
    values: {
      enableAutoReply: vacationMessage.enableAutoReply,
      restrictToContacts: vacationMessage.restrictToContacts,
      restrictToDomain: vacationMessage.restrictToDomain,
      dateRange:
        vacationMessage.startTime && vacationMessage.endTime
          ? [
              dayjs(new Date(vacationMessage.startTime)),
              dayjs(new Date(vacationMessage.endTime)),
            ]
          : [nextDay.current, dayAfter.current],
      responseSubject: vacationMessage.responseSubject
        ? vacationMessage.responseSubject
        : '',
      responseBodyHtml: vacationMessage.responseBodyPlainText
        ? vacationMessage.responseBodyPlainText
        : vacationMessage.responseBodyHtml
        ? vacationMessage.responseBodyHtml
        : '',
    },
  });

  const editorRef = useRef<Editor | null>(null);
  const htmlBody = watch('responseBodyHtml');

  function setHtmlBody() {
    if (editorRef.current) {
      setValue('responseBodyHtml', editorRef.current.getContent(), {
        shouldDirty: true,
      });
    }
  }

  const onSubmit = (data: FormInput) => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        UserRoles.SetGmailVacationMessage.value
      ) ||
      (dirtyFields.enableAutoReply &&
        getValues('enableAutoReply') === false &&
        isAbleToSeeUIElement(
          changeLogRoles,
          UserRoles.DisableGmailVacationMessage.value
        ))
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate({
        enableAutoReply: data.enableAutoReply,
        restrictToContacts: data.restrictToContacts,
        restrictToDomain: data.restrictToDomain,
        startTime: data.dateRange[0]!.toDate().getTime(),
        endTime: data.dateRange[1]!.toDate().getTime(),
        responseSubject: data.responseSubject,
        responseBodyHtml: data.responseBodyHtml,
      });
    }
  };

  const isEnableAutoReplyCheckbox = watch('enableAutoReply');
  const isRestrictToContactsCheckbox = watch('restrictToContacts');
  const isRestrictToDomainCheckbox = watch('restrictToDomain');

  const { mutate, isPending: isUpdating } = useMutation({
    mutationFn: (data: VacationModel) =>
      betaUpdateGmailVacationmessage(userEmail, data, changeLogMessage),
    onSuccess: (_voidData, formData) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Vacation message'),
      });
      queryClient.setQueryData([UserKeys.VacationMessage, userEmail], {
        ...formData,
      });
      setChangeLogMessage('');
      reset();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating(
          'the gmail vacation message of this user'
        ),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings
          .update_vacation_responder,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.vacationResponder,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardHeader
        action={
          <Tooltip
            arrow
            title={
              !userCanSetVacationMessage && !userCanToggleAutoReply
                ? Messages.Errors.Permission.Acting(
                    UserRoles.SetGmailVacationMessage.name
                  )
                : ''
            }
          >
            <span>
              <Button
                variant="contained"
                onClick={handleSubmit(onSubmit)}
                disabled={
                  !isDirty ||
                  (!userCanSetVacationMessage &&
                    !isAbleToSeeUIElement(
                      userRoles,
                      UserRoles.DisableGmailVacationMessage.value
                    ))
                }
                loading={isUpdating}
                color="primary"
                type="submit"
              >
                Save
              </Button>
            </span>
          </Tooltip>
        }
      />
      <CardContent
        sx={{
          px: 4,
          py: 0,
          flexGrow: 1,
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        <Stack gap={1} sx={{ overflowY: 'auto' }}>
          <Typography sx={{ pr: 8 }}>
            Vacation Responder: (Sends an automated reply to incoming messages.
            If a contact sends you several messages, this automated reply will
            be sent at most once every 4 days.)
          </Typography>
          <Stack
            flexDirection="row"
            alignItems="center"
            gap={2}
            sx={{
              color: !userCanToggleAutoReply ? 'text.disabled' : 'text.primary',
            }}
          >
            <Typography>Auto Reply:</Typography>
            <Stack flexDirection="row" alignItems="center" gap={1}>
              <Typography>Disabled</Typography>
              <Tooltip
                arrow
                title={
                  !userCanToggleAutoReply
                    ? Messages.Errors.Permission.Acting(
                        UserRoles.DisableGmailVacationMessage.name
                      )
                    : ''
                }
              >
                <span>
                  <Controller
                    name={'enableAutoReply'}
                    control={control}
                    render={({ field }) => {
                      return (
                        <Switch
                          onChange={() => {
                            setValue(
                              'enableAutoReply',
                              !isEnableAutoReplyCheckbox,
                              {
                                shouldDirty: true,
                              }
                            );
                          }}
                          checked={field.value === true}
                          disabled={
                            !isAbleToSeeUIElement(
                              userRoles,
                              UserRoles.DisableGmailVacationMessage.value
                            )
                          }
                        />
                      );
                    }}
                  />
                </span>
              </Tooltip>
              <Typography>Enabled</Typography>
            </Stack>
          </Stack>
          <Stack flexDirection="row" gap={2}>
            <Tooltip
              arrow
              title={
                !userCanSetVacationMessage
                  ? Messages.Errors.Permission.Acting(
                      UserRoles.SetGmailVacationMessage.name
                    )
                  : ''
              }
            >
              <span>
                <Controller
                  name={'restrictToContacts'}
                  control={control}
                  render={({ field }) => {
                    return (
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={() => {
                              setValue(
                                'restrictToContacts',
                                !isRestrictToContactsCheckbox,
                                {
                                  shouldDirty: true,
                                }
                              );
                            }}
                            checked={field.value === true}
                            disabled={!userCanSetVacationMessage}
                          />
                        }
                        label="Restrict Response to User's Contacts"
                      />
                    );
                  }}
                />
              </span>
            </Tooltip>
            <Tooltip
              arrow
              title={
                !userCanSetVacationMessage
                  ? Messages.Errors.Permission.Acting(
                      UserRoles.SetGmailVacationMessage.name
                    )
                  : ''
              }
            >
              <span>
                <Controller
                  name={'restrictToDomain'}
                  control={control}
                  render={({ field }) => {
                    return (
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={() => {
                              setValue(
                                'restrictToDomain',
                                !isRestrictToDomainCheckbox,
                                {
                                  shouldDirty: true,
                                }
                              );
                            }}
                            checked={field.value === true}
                            disabled={!userCanSetVacationMessage}
                          />
                        }
                        label="Restrict Response to Domain"
                      />
                    );
                  }}
                />
              </span>
            </Tooltip>
          </Stack>
          <Stack flexDirection="row" gap={2}>
            <Tooltip
              arrow
              title={
                !userCanSetVacationMessage
                  ? Messages.Errors.Permission.Acting(
                      UserRoles.SetGmailVacationMessage.name
                    )
                  : ''
              }
            >
              <span>
                <Controller
                  name={'responseSubject'}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      id="responseSubject"
                      label="Subject"
                      {...field}
                      sx={{ width: 300, mb: 3 }}
                      autoComplete="off"
                      disabled={!userCanSetVacationMessage}
                    />
                  )}
                />
              </span>
            </Tooltip>
            <Tooltip
              arrow
              title={
                !userCanSetVacationMessage
                  ? Messages.Errors.Permission.Acting(
                      UserRoles.SetGmailVacationMessage.name
                    )
                  : ''
              }
            >
              <span>
                <Controller
                  name={'dateRange'}
                  control={control}
                  rules={{
                    validate: {
                      required: (value) => {
                        if (!value[0]?.isValid() || !value[1]?.isValid()) {
                          return 'Both start and end dates are required';
                        }
                        return true;
                      },
                      endAfterStart: (value) => {
                        if (
                          value[1] &&
                          value[0] &&
                          value[1].isBefore(value[0])
                        ) {
                          return 'End date must be after the start date';
                        }
                        return true;
                      },
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <DateRangePicker
                      {...field}
                      disableHighlightToday
                      sx={{ width: 400 }}
                      label={'Vacation Dates'}
                      slots={{ field: SingleInputDateRangeField }}
                      slotProps={{
                        textField: {
                          error: !!error?.message,
                          helperText: error?.message ? error.message : ' ',
                        },
                      }}
                      disabled={!userCanSetVacationMessage}
                    />
                  )}
                />
              </span>
            </Tooltip>
          </Stack>
          <Box sx={{ height: 400 }}>
            <Tooltip
              arrow
              title={
                !userCanSetVacationMessage
                  ? Messages.Errors.Permission.Acting(
                      UserRoles.SetGmailVacationMessage.name
                    )
                  : ''
              }
            >
              <span>
                <TextEditor
                  editorRef={editorRef}
                  content={htmlBody}
                  setContent={setHtmlBody}
                  disabled={!userCanSetVacationMessage}
                />
              </span>
            </Tooltip>
          </Box>
        </Stack>
      </CardContent>
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() =>
          mutate({
            enableAutoReply: getValues('enableAutoReply'),
            restrictToContacts: getValues('restrictToContacts'),
            restrictToDomain: getValues('restrictToDomain'),
            startTime: getValues('dateRange')[0]!.toDate().getTime(),
            endTime: getValues('dateRange')[1]!.toDate().getTime(),
            responseSubject: getValues('responseSubject'),
            responseBodyHtml: getValues('responseBodyHtml'),
          })
        }
        mutateLoading={isUpdating}
      />
    </Card>
  );
}
